import React from 'react'
import { createRoot, IReactRoot, TReactRenderTarget } from '@aytm/lib/common/reactCreateRoot'
import loaderAction from '@aytm/app/swidget/base/actions/loader/loaderAction'

// PALETTES [
import 'css_palettes/swidget.css'
import 'css_palettes/primereact-swidget.css'
// ]

// PRIMEREACT CSS [
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import 'primereact-umongous-theme/theme.min.css'
import 'primereact/resources/primereact.min.css'
// ]

import {
  CONFIG_SESSION_ID,
  ISurveyWidget,
  ISurveyWidgetSessionProps,
  ISurveyWidgetSessionPropsLayout,
  ISurveyWidgetSessionPropsTheme
} from './types'
import { ActionType, IAction, SurveyMode } from './actions/types'
import { IModal, ModalID } from './modules/modal/types'
import { ISessionAPI } from './session/types'

import ModuleManager from './modules/Module.Manager'
import ClientModule from './modules/client/Module'
import ModalModule from './modules/modal/Module'
import ViewportModule from './modules/viewport/Module'

import Session from './session/Session'
import HooksModule from './modules/hooks/Module'

export default function SurveyWidget(): ISurveyWidget {
  let currentAction: IAction = null
  let currentTarget: TReactRenderTarget | ModalID = null
  let currentModal: IModal = null
  let currentReactRoot: IReactRoot = null
  let sessionAPI: ISessionAPI = null
  let sessionProps: ISurveyWidgetSessionProps = {
    layout: ISurveyWidgetSessionPropsLayout.DEFAULT,
    theme: ISurveyWidgetSessionPropsTheme.DEFAULT,
    custom: {}
  }

  const unmount = () => {
    // unmount react root
    if (currentReactRoot) {
      currentReactRoot.unmount()
      // remove viewport emulation
      ViewportModule.emulate()
    }
    // unmount modal
    if (currentModal) {
      currentModal.unmount()
      currentTarget = null
      currentModal = null
      currentReactRoot = null
    }
    if (HooksModule.hasClosePreviewHook) {
      HooksModule.doClosePreview()
    }
  }

  const sessionAPIRef = (api: ISessionAPI) => {
    sessionAPI = api
  }
  const render = () => {
    if (currentReactRoot) {
      currentReactRoot.render(
        <Session action={currentAction} sessionProps={sessionProps} onClosePreview={unmount} ref={sessionAPIRef} />
      )
    }
  }

  const widget: ISurveyWidget = {
    action(action) {
      currentAction = action
      render()
      return widget
    },
    async loadAction(url, payloadOrTransformLoadAction, apply = true) {
      let loadAction: IAction = {
        action: ActionType.LOAD,
        action_url: url
      }
      if ('function' === typeof payloadOrTransformLoadAction) {
        loadAction = payloadOrTransformLoadAction(loadAction)
      } else {
        loadAction.payload = payloadOrTransformLoadAction
        if (payloadOrTransformLoadAction?.data_values)
          ClientModule.data_values = payloadOrTransformLoadAction.data_values
      }
      const loadedAction = await ClientModule.loadAction(loadAction)
      if (apply) {
        widget.action(loadedAction)
      }
      return loadedAction
    },
    async loadTarget(target, initURL, mode = SurveyMode.XRAYS, payload, verify = false) {
      const initAction = await widget.loadAction(initURL, payload, false)
      const targetAction = await ClientModule.requestJumpToQuestion(initAction, mode, target)

      const failed = ActionType.ERROR === initAction.action || ActionType.ERROR === targetAction.action
      if (!(verify && failed)) {
        widget.action(loaderAction(initAction))
        if (target) widget.action(targetAction)
      }

      return [initAction, targetAction]
    },
    async loadLocale(locale, target, initURL, mode = SurveyMode.XRAYS) {
      const initAction = await ClientModule.requestSetLocale(
        { action_url: initURL, action: ActionType.SHOW },
        mode,
        locale
      )
      const targetAction = await ClientModule.requestJumpToQuestion(initAction, mode, target)

      ClientModule.locale = locale
      widget.action(loaderAction(initAction))
      if (target) widget.action(targetAction)

      return targetAction
    },
    loader({ mode }) {
      widget.action(loaderAction({ mode }))
      return widget
    },
    config(moduleID, props) {
      if (CONFIG_SESSION_ID === moduleID) {
        sessionProps = Object.assign({}, sessionProps, props as Partial<ISurveyWidgetSessionProps>)
        render()
      } else {
        ModuleManager.config(moduleID, props)
      }
      return widget
    },
    render(target) {
      if (currentTarget !== target) {
        unmount()
        currentTarget = target
        if ('string' === typeof target) {
          currentModal = ModalModule.modal(target)
          if (currentModal?.active) {
            const renderTarget = currentModal.mount()
            currentReactRoot = createRoot(renderTarget)
          }
        } else {
          currentReactRoot = createRoot(currentTarget as TReactRenderTarget)
        }
      }
      render()
      return widget
    },
    unmount(root: TReactRenderTarget) {
      if (root) {
        currentReactRoot = createRoot(root)
      }
      unmount()
      currentReactRoot = null
    },

    get currentAction() {
      return sessionAPI?.action
    },
    applyCustomStyle(style) {
      sessionAPI?.applyCustomStyle(style)
      return widget
    }
  }
  return widget
}
